import { Fragment, useEffect, useState, useCallback } from "react";
import { useApiClient } from "../api/useApiClient";
import type { ReviewItemArchivado } from "../types/reviewItem";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { HistorialRow, HistorialToggle } from "../components/HistorialRow";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { AvisoError } from "../components/AvisoError";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha, formatFechaHoraUtc } from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";

/**
 * Archivadas — [NUEVO 20/10/2026, S-6a] solo lectura y solo Contaduría.
 *
 * Una factura llega acá únicamente desde Rechazadas (botón "Archivar"). Esta
 * pantalla NO tiene acciones a propósito: recuperar una archivada es un cambio
 * de estado que el sector todavía no definió (S-6b, Entrega 3).
 */
export function ArchivadasPage() {
  const api = useApiClient();
  const [items, setItems] = useState<ReviewItemArchivado[]>([]);
  const [lineas, setLineas] = useState<Set<string>>(new Set());
  const [historial, setHistorial] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const columnas = 7;

  const tabla = useTablaBandeja(items, {
    camposBusqueda: ["proveedor_nombre", "rut_emisor", "nro", "serie", "rejection_reason"],
    ordenInicial: { columna: "archivado_at", direccion: "desc" },
    camposNumericos: ["monto"],
    claveEstable: "id",
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await api.listarArchivadas());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido al cargar la lista.");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  function alternar(setter: typeof setLineas, id: string) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf") {
    try {
      abrirEnNuevaPestana(await api.obtenerBlobSasUrl(id, tipo));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  if (loading) return <p>Cargando...</p>;

  return (
    <div>
      <h1>Archivadas</h1>
      <p className="subtitle">
        Facturas rechazadas que Contaduría archivó. Solo consulta: se muestran las 1.000 archivadas más recientes.
      </p>

      <AvisoError mensaje={error} onCerrar={() => setError(null)} />

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={tabla.filtradas}
        totalSinFiltro={items.length}
        placeholder="Buscar por proveedor, RUT, numero de CFE o motivo..."
      />

      <table className="compact">
        {/* Suma 100%: 14+17+9+8+20+12+20. */}
        <colgroup>
          <col style={{ width: "14%" }} />
          <col style={{ width: "17%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "20%" }} />
        </colgroup>
        <thead>
          <tr>
            <ThOrdenable columna="nro" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Documento
            </ThOrdenable>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="monto" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Monto
            </ThOrdenable>
            <ThOrdenable columna="fecha_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Fecha doc.
            </ThOrdenable>
            <th>Motivo de rechazo</th>
            <ThOrdenable columna="archivado_at" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Archivada
            </ThOrdenable>
            <th>Documentos</th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={columnas} className="empty-row">
                {items.length === 0 ? "No hay facturas archivadas." : "Ninguna factura coincide con la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item) => (
            <Fragment key={item.id}>
              <tr>
                <td>
                  <CfeIdentidad
                    tipoCfe={item.tipo_cfe}
                    serie={item.serie}
                    nro={item.nro}
                    rutEmisor={item.rut_emisor}
                  />
                </td>
                <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                  {item.proveedor_nombre}
                </td>
                <td className="mono celda-monto">
                  {formatMonto(item.monto)}
                  <span className="moneda">{item.moneda}</span>
                </td>
                <td className="mono">{formatFecha(item.fecha_documento)}</td>
                <td className="rejection-reason">{item.rejection_reason}</td>
                <td className="mono wrap-ok" title={item.archivado_por ?? undefined}>
                  {formatFechaHoraUtc(item.archivado_at)}
                </td>
                <td className="actions-cell">
                  <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                    XML
                  </button>
                  {item.pdf_blob_url && (
                    <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                      PDF
                    </button>
                  )}
                  <LineasToggle expanded={lineas.has(item.id)} onToggle={() => alternar(setLineas, item.id)} />
                  <HistorialToggle
                    expanded={historial.has(item.id)}
                    onToggle={() => alternar(setHistorial, item.id)}
                  />
                </td>
              </tr>
              {lineas.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={columnas} />}
              {historial.has(item.id) && <HistorialRow reviewItemId={item.id} colSpan={columnas} />}
            </Fragment>
          ))}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />
    </div>
  );
}
